import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Slider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  SelectChangeEvent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Divider
} from '@mui/material';
import {
  Compress as CompressIcon,
  Download as DownloadIcon,
  Info as InfoIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  FolderZip as FolderZipIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import fileService from '../../services/fileService';
import api from '../../services/api';
import { useSnackbar } from 'notistack';

interface FileCompressorProps {
  fileId: string;
  fileName: string;
  fileSize: number;
  fileType: string;
  onCompressionComplete?: (newFileId: string) => void;
}

interface CompressionResponse {
  success: boolean;
  fileId?: string;
  size?: number;
  compressionRatio?: number;
  downloadUrl?: string;
  error?: string;
}

interface FolderDownloadProgressProps {
  downloadId: string;
  onComplete: () => void;
  onCancel: () => void;
}

const FolderDownloadProgress: React.FC<FolderDownloadProgressProps> = ({ 
  downloadId, 
  onComplete, 
  onCancel 
}) => {
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState('preparing');
  const [folderName, setFolderName] = useState('');
  const [total, setTotal] = useState(0);
  const [processed, setProcessed] = useState(0);
  const [error, setError] = useState('');
  const { enqueueSnackbar } = useSnackbar();
  
  const checkProgress = useCallback(async () => {
    try {
      const response = await api.get(`/api/folders/download/${downloadId}/progress`);
      const data = response.data;
      
      setStatus(data.status);
      setProgress(data.progress || 0);
      setFolderName(data.folderName || '');
      setTotal(data.total || 0);
      setProcessed(data.processed || 0);
      
      if (data.error) {
        setError(data.error);
      }
      
      if (data.status === 'completed') {
        enqueueSnackbar(`Download of ${data.folderName}.zip completed!`, { variant: 'success' });
        onComplete();
      } else if (data.status === 'failed') {
        enqueueSnackbar(`Download failed: ${data.error || 'Unknown error'}`, { variant: 'error' });
      } else if (data.status === 'cancelled') {
        enqueueSnackbar('Download was cancelled', { variant: 'warning' });
        onComplete();
      } else {
        // Continue polling if still in progress
        setTimeout(checkProgress, 1000);
      }
    } catch (err) {
      setError('Failed to check download progress');
      setTimeout(checkProgress, 3000);
    }
  }, [downloadId, enqueueSnackbar, onComplete]);
  
  useEffect(() => {
    checkProgress();
    
    return () => {
      // Nothing to clean up
    };
  }, [checkProgress]);
  
  const handleCancel = async () => {
    try {
      await api.post(`/api/folders/download/${downloadId}/cancel`);
      enqueueSnackbar('Download cancellation requested', { variant: 'info' });
    } catch (err) {
      enqueueSnackbar('Failed to cancel download', { variant: 'error' });
    }
    onCancel();
  };
  
  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="subtitle1">
          {status === 'preparing' ? 'Preparing download...' : 
           status === 'processing' ? `Downloading ${folderName}.zip` : 
           status === 'completed' ? 'Download complete!' : 
           status === 'cancelled' ? 'Download cancelled' : 
           'Download failed'}
        </Typography>
        {(status === 'preparing' || status === 'processing') && (
          <IconButton onClick={handleCancel} size="small" color="error">
            <CancelIcon />
          </IconButton>
        )}
      </Box>
      
      <LinearProgress 
        variant="determinate" 
        value={progress} 
        color={
          status === 'completed' ? 'success' : 
          status === 'failed' ? 'error' : 
          status === 'cancelled' ? 'warning' : 
          'primary'
        }
        sx={{ height: 10, borderRadius: 5 }}
      />
      
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
        <Typography variant="body2" color="text.secondary">
          {processed} of {total} files
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {progress}%
        </Typography>
      </Box>
      
      {error && (
        <Typography variant="body2" color="error" sx={{ mt: 1 }}>
          Error: {error}
        </Typography>
      )}
    </Box>
  );
};

interface FolderDownloadDialogProps {
  open: boolean;
  onClose: () => void;
  folderId: string;
  folderName: string;
}

const FolderDownloadDialog: React.FC<FolderDownloadDialogProps> = ({
  open,
  onClose,
  folderId,
  folderName
}) => {
  const [downloading, setDownloading] = useState(false);
  const [downloadId, setDownloadId] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const { enqueueSnackbar } = useSnackbar();
  
  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      setDownloading(false);
      setDownloadId(null);
      setProgress(0);
      setError(null);
    }
  }, [open]);
  
  // Poll for progress updates
  useEffect(() => {
    let intervalId: NodeJS.Timeout;
    
    if (downloadId && downloading) {
      intervalId = setInterval(async () => {
        try {
          const response = await api.get(`/folders/download/${downloadId}/progress`);
          const { progress: currentProgress, status } = response.data;
          
          setProgress(currentProgress);
          
          if (status === 'completed') {
            setDownloading(false);
            clearInterval(intervalId);
            
            // Trigger download with authentication
            try {
              const response = await api.get(`/folders/${folderId}/download`, {
                responseType: 'blob'
              });
              
              // Create blob URL and trigger download
              const blob = new Blob([response.data], { type: 'application/zip' });
              const url = window.URL.createObjectURL(blob);
              const link = document.createElement('a');
              link.href = url;
              link.download = `${folderName || 'folder'}.zip`;
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              window.URL.revokeObjectURL(url);
              
              enqueueSnackbar('Download completed successfully!', { variant: 'success' });
            } catch (downloadError) {
              setError('Failed to download ZIP file');
              console.error('Download error:', downloadError);
            }
          } else if (status === 'failed') {
            setDownloading(false);
            setError('Download preparation failed. Please try again.');
            clearInterval(intervalId);
          }
        } catch (error) {
          setError('Failed to check download progress');
          setDownloading(false);
          clearInterval(intervalId);
        }
      }, 1000);
    }
    
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [downloadId, downloading, folderId, folderName, enqueueSnackbar]);
  
  const handleDownload = async () => {
    try {
      setDownloading(true);
      setError(null);
      
      // Request download preparation
      const response = await api.post(`/folders/${folderId}/download/prepare`);
      const { downloadId: generatedId } = response.data;
      setDownloadId(generatedId);
      
      enqueueSnackbar('Preparing your download...', { variant: 'info' });
    } catch (error) {
      setError('Failed to prepare download. Please try again.');
      setDownloading(false);
    }
  };
  
  const handleCancel = async () => {
    if (downloadId) {
      try {
        await api.post(`/folders/download/${downloadId}/cancel`);
        enqueueSnackbar('Download cancelled', { variant: 'info' });
      } catch (error) {
      }
    }
    
    setDownloading(false);
    onClose();
  };
  
  return (
    <Dialog open={open} onClose={downloading ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Download Folder as ZIP
      </DialogTitle>
      
      <DialogContent>
        <Typography variant="body1" gutterBottom>
          Preparing to download <strong>{folderName}</strong> as a ZIP archive.
        </Typography>
        
        {error && (
          <Alert severity="error" sx={{ mt: 2, mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {downloading && (
          <Box sx={{ mt: 3, mb: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {progress < 100 
                ? 'Preparing your download...' 
                : 'Download ready, starting automatically...'}
            </Typography>
            
            <LinearProgress 
              variant="determinate" 
              value={progress} 
              sx={{ height: 8, borderRadius: 4, mt: 1, mb: 1 }} 
            />
            
            <Typography variant="body2" color="text.secondary" align="right">
              {progress}%
            </Typography>
          </Box>
        )}
        
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          This process may take some time depending on the folder size.
          The ZIP file will download automatically once prepared.
        </Typography>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={handleCancel} disabled={downloading && progress > 95}>
          {downloading ? 'Cancel' : 'Close'}
        </Button>
        {!downloading && (
          <Button 
            onClick={handleDownload} 
            variant="contained" 
            color="primary"
            startIcon={<DownloadIcon />}
          >
            Start Download
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export const useFolderDownload = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [folderData, setFolderData] = useState<{ id: string; name: string } | null>(null);
  
  const openFolderDownloadDialog = (folderId: string, folderName: string) => {
    setFolderData({ id: folderId, name: folderName });
    setIsOpen(true);
  };
  
  const closeFolderDownloadDialog = () => {
    setIsOpen(false);
  };
  
  const FolderDownloadDialogComponent = () => (
    folderData && (
      <FolderDownloadDialog
        open={isOpen}
        onClose={closeFolderDownloadDialog}
        folderId={folderData.id}
        folderName={folderData.name}
      />
    )
  );
  
  return {
    openFolderDownloadDialog,
    closeFolderDownloadDialog,
    FolderDownloadDialogComponent
  };
};

export const FileCompressor: React.FC<FileCompressorProps> = ({
  fileId,
  fileName,
  fileSize,
  fileType,
  onCompressionComplete
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [compressionResult, setCompressionResult] = useState<{
    newFileId?: string;
    compressedSize?: number;
    compressionRatio?: number;
    downloadUrl?: string;
  } | null>(null);

  const [compressionOptions, setCompressionOptions] = useState({
    quality: 80,
    format: '',
    width: 0,
    height: 0,
    maintainAspectRatio: true
  });

  const isImageFile = () => {
    return fileType.startsWith('image/');
  };

  const isPDFFile = () => {
    return fileType === 'application/pdf';
  };

  const isCompressible = () => {
    // Files that can be compressed
    return (
      isImageFile() ||
      isPDFFile() ||
      fileType.includes('zip') ||
      fileType.includes('document') ||
      fileType.includes('spreadsheet') ||
      fileType.includes('presentation')
    );
  };

  const getDefaultFormat = () => {
    if (fileType === 'image/png') return 'png';
    if (fileType === 'image/jpeg' || fileType === 'image/jpg') return 'jpeg';
    if (fileType === 'image/gif') return 'gif';
    if (fileType === 'image/webp') return 'webp';
    if (fileType === 'application/pdf') return 'pdf';
    return '';
  };

  const handleQualityChange = (_event: Event, newValue: number | number[]) => {
    setCompressionOptions({
      ...compressionOptions,
      quality: newValue as number
    });
  };

  const handleFormatChange = (event: SelectChangeEvent<string>) => {
    setCompressionOptions({
      ...compressionOptions,
      format: event.target.value as string
    });
  };

  const handleDimensionChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    dimension: 'width' | 'height'
  ) => {
    const value = parseInt(event.target.value, 10) || 0;
    setCompressionOptions({
      ...compressionOptions,
      [dimension]: value
    });
  };

  const handleCompressFile = async () => {
    if (!isCompressible()) {
      setError('This file type cannot be compressed');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setSuccess(false);
      setCompressionResult(null);

      // Use the current format or the original format if none selected
      const format = compressionOptions.format || getDefaultFormat();
      
      const options: {
        format: string;
        quality: number;
        width?: number;
        height?: number;
      } = {
        ...compressionOptions,
        format
      };
      
      // Skip empty or 0 dimensions
      if (options.width !== undefined && options.width <= 0) delete options.width;
      if (options.height !== undefined && options.height <= 0) delete options.height;
      
      const response = await fileService.compressFile(fileId, options) as CompressionResponse;
      
      if (response.success) {
        setSuccess(true);
        setCompressionResult({
          newFileId: response.fileId || '',
          compressedSize: response.size || 0,
          compressionRatio: response.compressionRatio || 0,
          downloadUrl: response.downloadUrl || ''
        });
        
        if (onCompressionComplete && response.fileId) {
          onCompressionComplete(response.fileId);
        }
      } else {
        setError(response.error || 'Compression failed');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Compression failed');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadCompressed = () => {
    if (compressionResult?.newFileId) {
      fileService.downloadFile(compressionResult.newFileId);
    } else if (compressionResult?.downloadUrl) {
      window.open(compressionResult.downloadUrl, '_blank');
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatCompressibilityScore = (): string => {
    if (isImageFile() && !fileType.includes('svg')) {
      return 'High';
    } else if (isPDFFile() || fileType.includes('document')) {
      return 'Medium';
    } else if (fileType.includes('zip') || fileType.includes('rar')) {
      return 'Low (already compressed)';
    } else {
      return 'Unknown';
    }
  };

  if (!isCompressible()) {
    return (
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center' }}>
          <CompressIcon sx={{ mr: 1, color: 'text.secondary' }} />
          File Compression
        </Typography>
        <Alert severity="info" sx={{ mt: 2 }}>
          This file type cannot be compressed. Compression is available for images, PDFs, and documents.
        </Alert>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 2, mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <CompressIcon sx={{ mr: 1, color: 'primary.main' }} />
        <Typography variant="subtitle1">Compress File</Typography>
      </Box>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6}>
          <Typography variant="body2" color="text.secondary">
            Current Size: {formatFileSize(fileSize)}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center' }}>
            Compressibility: {formatCompressibilityScore()}
            <Tooltip title="The potential for size reduction depends on the file type and content. Images, PDFs, and documents typically compress well.">
              <IconButton size="small">
                <InfoIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Typography>
        </Grid>
      </Grid>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && compressionResult && (
        <Alert severity="success" sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <CheckIcon fontSize="small" sx={{ mr: 1 }} />
            File compressed successfully!
          </Typography>
          <Typography variant="body2">
            Original: {formatFileSize(fileSize)} → Compressed: {formatFileSize(compressionResult.compressedSize || 0)}
          </Typography>
          <Typography variant="body2">
            Saved: {formatFileSize(fileSize - (compressionResult.compressedSize || 0))} 
            ({Math.round((1 - (compressionResult.compressedSize || fileSize) / fileSize) * 100)}% reduction)
          </Typography>
          <Button 
            startIcon={<DownloadIcon />}
            onClick={handleDownloadCompressed}
            size="small"
            variant="outlined"
            sx={{ mt: 1 }}
          >
            Download Compressed File
          </Button>
        </Alert>
      )}

      <Box sx={{ mb: 3 }}>
        <Typography gutterBottom>Compression Quality</Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs>
            <Slider
              value={compressionOptions.quality}
              onChange={handleQualityChange}
              aria-labelledby="compression-quality-slider"
              valueLabelDisplay="auto"
              step={5}
              marks
              min={10}
              max={100}
            />
          </Grid>
          <Grid item>
            <Typography variant="body2" color="text.secondary">
              {compressionOptions.quality}%
            </Typography>
          </Grid>
        </Grid>
        <Typography variant="caption" color="text.secondary">
          Lower quality = smaller file size, higher quality = better image fidelity
        </Typography>
      </Box>

      {isImageFile() && (
        <>
          <Box sx={{ mb: 3 }}>
            <FormControl fullWidth size="small">
              <InputLabel id="format-select-label">Output Format</InputLabel>
              <Select
                labelId="format-select-label"
                value={compressionOptions.format || getDefaultFormat()}
                onChange={handleFormatChange}
                label="Output Format"
              >
                <MenuItem value="jpeg">JPEG (best for photos)</MenuItem>
                <MenuItem value="png">PNG (supports transparency)</MenuItem>
                <MenuItem value="webp">WebP (modern format, smaller size)</MenuItem>
                <MenuItem value="gif">GIF (for animations)</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography gutterBottom>Resize Dimensions (optional)</Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  label="Width (px)"
                  type="number"
                  value={compressionOptions.width || ''}
                  onChange={(e) => handleDimensionChange(e, 'width')}
                  size="small"
                  InputProps={{
                    endAdornment: <InputAdornment position="end">px</InputAdornment>,
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Height (px)"
                  type="number"
                  value={compressionOptions.height || ''}
                  onChange={(e) => handleDimensionChange(e, 'height')}
                  size="small"
                  InputProps={{
                    endAdornment: <InputAdornment position="end">px</InputAdornment>,
                  }}
                />
              </Grid>
            </Grid>
            <Typography variant="caption" color="text.secondary">
              Leave empty to maintain original dimensions
            </Typography>
          </Box>
        </>
      )}

      <Box sx={{ mt: 3 }}>
        <Button
          variant="contained"
          startIcon={<CompressIcon />}
          onClick={handleCompressFile}
          disabled={loading}
        >
          {loading ? (
            <>
              <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
              Compressing...
            </>
          ) : (
            'Compress File'
          )}
        </Button>
      </Box>
    </Paper>
  );
};

export default useFolderDownload; 